// Enroll this daemon's client-identity pubkey as a `devices` row under the
// host owner.
//
// Fire-and-forget on every boot. Idempotent on the server (upsert keyed by
// userId + pubkey). If the auth-server is unreachable or rejects, the daemon
// keeps running — outbound cross-host SSH is disabled until the next boot
// resolves the issue, but inbound + everything else is unaffected.

import type { Logger } from "pino";

const REQUEST_TIMEOUT_MS = 10_000;

export interface EnrollDaemonClientIdentityArgs {
  logger: Logger;
  authServerUrl: string;
  daemonToken: string;
  pubkey: string;
  /** Used as the default device label so revoke UIs can distinguish daemons. */
  serverId: string;
}

export async function enrollDaemonClientIdentity(
  args: EnrollDaemonClientIdentityArgs,
): Promise<void> {
  const { authServerUrl, daemonToken, pubkey, serverId } = args;
  const log = args.logger.child({ component: "cross-host-enroll" });

  const ac = new AbortController();
  const t = setTimeout(() => ac.abort(), REQUEST_TIMEOUT_MS);
  try {
    const url = new URL("/daemon/devices/pubkey", authServerUrl).toString();
    const res = await fetch(url, {
      method: "POST",
      headers: {
        authorization: `Bearer ${daemonToken}`,
        "content-type": "application/json",
      },
      body: JSON.stringify({ pubkey, label: `daemon:${serverId}` }),
      signal: ac.signal,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "(unreadable)");
      log.warn({ status: res.status, text }, "cross_host_enroll_failed");
      return;
    }
    log.info({ serverId }, "cross_host_enroll_ok");
  } catch (err) {
    // Network errors, AbortError on timeout, etc. Always swallowed — never
    // crash the boot for a feature that isn't load-bearing on this side.
    log.warn({ err }, "cross_host_enroll_threw");
  } finally {
    clearTimeout(t);
  }
}
